import React, { useEffect, useState } from 'react';
import { Alert, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  useGetUserOverviewQuery,
  useDeleteUserMutation,
  useLazyGetSubscriptionTypeQuery,
  useAddActionNoteMutation,
  useReSubscribeMutation
} from '../../../../store/api/userManagementApi.js';
import '../styles.css';
import UserUpdateModal from '../shared/user_update.model.jsx';
import AddCommentModal from '../shared/add_note.model.jsx';
import PricingPlanModal from '../shared/pricing_plan.model.jsx';

const ProfileEditTab = ({ onSaveSuccess, groupMemberView = false }) => {
  const defaultResubscribeWarning = 'Resubscribe action will deduct ammount from the customer\'s original payment method. Please ensure the customer is aware of this before confirming.';
  const userData = useSelector(state => state.userManagement.userData) || {};
  const resolvedUserId = useSelector(state => state.userManagement.resolvedUserId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isResubscribeCommentModalOpen, setIsResubscribeCommentModalOpen] = useState(false);
  const [pendingDeleteUserId, setPendingDeleteUserId] = useState(null);
  const [pendingResubscribeData, setPendingResubscribeData] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [resubscribeWarning, setResubscribeWarning] = useState(defaultResubscribeWarning);
  const [pgResubscribeNeeded, setPgResubscribeNeeded] = useState(false);
  const [isFetchingResubscribePlans, setIsFetchingResubscribePlans] = useState(false);
  const [isResubscribeInProgress, setIsResubscribeInProgress] = useState(false);

  const { data: userOverviewResponse, refetch: refetchUserOverview } = useGetUserOverviewQuery(resolvedUserId, { skip: !resolvedUserId });
  const [triggerGetSubscriptionType, { data: subscriptionTypeResponse }] = useLazyGetSubscriptionTypeQuery();
  const [deleteUserMutation] = useDeleteUserMutation();
  const [addActionNoteMutation] = useAddActionNoteMutation();
  const [reSubscribeMutation] = useReSubscribeMutation();
  const overviewUserData = userOverviewResponse?.data?.[0] || {};
  const overviewWarningMessage = typeof overviewUserData?.warning === 'string'
    ? overviewUserData.warning.trim()
    : '';
  const editProfileUserData = {
    ...userData,
    ...overviewUserData,
    user_id: overviewUserData?.user_id || userData?.user_id,
    first_name: overviewUserData?.first_name || userData?.first_name,
    last_name: overviewUserData?.last_name || userData?.last_name,
    user_email: overviewUserData?.user_email || overviewUserData?.email || userData?.user_email,
    phone: overviewUserData?.phone || overviewUserData?.phone_number || userData?.phone,
    country_code: overviewUserData?.country_code || overviewUserData?.country || userData?.country_code,
    status: overviewUserData?.status || userData?.status,
    password: overviewUserData?.password || userData?.password,
  };
  const lastLoggedIn = overviewUserData?.last_logged_in || userData?.last_logged_in || '—';
  const lastUsed = overviewUserData?.last_used || userData?.last_used || '—';

  useEffect(() => {
    try {
      const configStr = localStorage.getItem('publisherConfig');
      if (configStr) {
        const configArr = JSON.parse(configStr);
        const found = configArr.find(item => item.key_name === 'pg_resubscribe_needed');
        setPgResubscribeNeeded(found ? (found.key_value === true || found.key_value === 'True') : false);
      }
    } catch (e) {
      setPgResubscribeNeeded(false);
    }
  }, []);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleUpdateSuccess = updatedData => {
    if (onSaveSuccess) onSaveSuccess(updatedData);
  };

  const deleteUser = async () => {
    var r = window.confirm('Do you want to delete ?');
    if (r == true) {
      setPendingDeleteUserId(resolvedUserId);
      setIsCommentModalOpen(true);
    }
  };

  const handleDeleteCommentSubmit = async note => {
    try {
      const commentData = {
        action_type: 'user-delete',
        note: note,
        user_id: pendingDeleteUserId
      };

      await addActionNoteMutation(commentData).unwrap();

      setIsCommentModalOpen(false);
      if (pendingDeleteUserId) {
        proceedWithDelete(pendingDeleteUserId);
      }
    } catch (error) {
      console.error('Comment submission error:', error);
      setIsCommentModalOpen(false);
      if (pendingDeleteUserId) {
        proceedWithDelete(pendingDeleteUserId);
      }
    }
  };

  const proceedWithDelete = async userIdToDelete => {
    const result = await deleteUserMutation(userIdToDelete).unwrap();
    if (result) {
      window.location.href = '/operations/UserManagment';
    } else {
      console.error('Delete user failed:', result);
    }
    setPendingDeleteUserId(null);
  };

  const mapPricingPlans = (plans = []) => {
    return plans.map(plan => ({
      label: plan.subscription_name || plan.subscription_type_name,
      subscription_type_id: plan.subscription_type_id,
      price: plan.price,
      interval: plan.interval,
      subscription_type_name: plan.subscription_type_name || plan.subscription_name,
      symbol: plan.symbol,
      logo: plan.logo,
      description: plan.description,
      publisher_subscription_id: plan.publisher_subscription_id || plan.subscription_type_id || plan.sub_id,
      last_subscription_id: plan.last_subscription_id
    }));
  };

  const handleOpenResubscribeModal = async () => {
    const userId = resolvedUserId || userData?.user_id;
    if (!userId) {
      toast.error('Unable to identify user for resubscribe');
      return;
    }

    setIsFetchingResubscribePlans(true);
    setSelectedPlan(null);
    setResubscribeWarning(defaultResubscribeWarning);

    try {
      let plansResponse = subscriptionTypeResponse;
      if (!plansResponse) {
        const result = await triggerGetSubscriptionType().unwrap();
        plansResponse = result;
      }
      const plansData = Array.isArray(plansResponse) ? plansResponse : plansResponse?.data;

      if (plansData?.length) {
        setPricingPlans(mapPricingPlans(plansData));
        setShowPlanModal(true);
        return;
      }

      setPricingPlans([]);
      toast.warning(plansResponse?.message || 'No pricing plans available for resubscribe');
    } catch (error) {
      console.log(error, 'error while fetching subscription types');
      toast.error('Failed to load pricing plans');
    } finally {
      setIsFetchingResubscribePlans(false);
    }
  };

  const handleResubscribeConfirm = async () => {
    if (!selectedPlan || isResubscribeInProgress) {
      return;
    }

    const userId = resolvedUserId || userData?.user_id;
    if (!userId) {
      toast.error('Unable to identify user for resubscribe');
      return;
    }

    setPendingResubscribeData({
      userId,
      plan: selectedPlan
    });
    setShowPlanModal(false);
    setIsResubscribeCommentModalOpen(true);
  };

  const handleResubscribeCommentSubmit = async note => {
    if (!pendingResubscribeData) {
      return;
    }

    const { userId, plan } = pendingResubscribeData;

    const commentData = {
      action_type: 'resubscribe',
      note: note,
      user_id: userId
    };

    setIsResubscribeInProgress(true);
    try {
      try {
        const commentResponse = await addActionNoteMutation(commentData).unwrap();
        if (commentResponse.success) {
          console.log('Comment added successfully');
        } else {
          console.warn('Failed to add note:', commentResponse.message);
        }
      } catch (error) {
        console.warn('Error adding note:', error);
      }

      const response = await reSubscribeMutation({
        user_subscription_id: overviewUserData?.stripe_subscription_id,
        uid: userId,
        sub_id: plan?.publisher_subscription_id
      }).unwrap();

      if (response?.success) {
        setIsResubscribeCommentModalOpen(false);
        setSelectedPlan(null);
        setResubscribeWarning(defaultResubscribeWarning);
        setPendingResubscribeData(null);
        toast.success('Subscription re-subscribed successfully');
        refetchUserOverview();
        return;
      }

      if (response?.success === false && response?.message === 'requires_action') {
        setResubscribeWarning('Resubscribe initiated but requires customer action. Please ask customer to complete the action from website.');
        return;
      }

      setResubscribeWarning(response?.message || 'An error occurred while executing Resubscribe action.');
    } catch (error) {
      toast.error(error?.data?.message ||'An error occurred while executing Resubscribe action');
      setResubscribeWarning('An error occurred while executing Resubscribe action.');
    } finally {
      setIsResubscribeInProgress(false);
    }
  };

  return (
    <>
      {!!overviewWarningMessage && !overviewUserData?.resubscribe_eligible && (
        <Alert color="warning" style={{ fontSize: 14 }}>
          {overviewWarningMessage}
        </Alert>
      )}

      {(pgResubscribeNeeded && overviewUserData?.resubscribe_eligible && overviewUserData?.stripe_subscription_id) && (
        <Alert color="warning" style={{ fontSize: 14 }} className="d-flex justify-content-between align-items-center">
          <span>
            User does not have any active subscription. Please resubscribe.
          </span>
          <Button color="primary" className="mb-2 mt-2" onClick={handleOpenResubscribeModal} disabled={isFetchingResubscribePlans}>
            {isFetchingResubscribePlans ? 'Loading...' : 'Resubscribe'}
          </Button>
        </Alert>
      )}

      <div className="d-flex mb-2 p-2 flex-column flex-md-row align-items-center" style={{ width: '100%' }}>
        <div className="d-flex align-items-center" style={{ width: '80%' }}>
          <div
            className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center"
            style={{ width: '48px', height: '48px', fontSize: '1.25rem' }}
          >
            {userData.user_image ? (
              <img
                src={userData.user_image}
                alt="User Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
              />
            ) : (
              userData.first_name?.[0]?.toUpperCase() || 'U'
            )}
          </div>
          <div style={{ padding: '10px' }}>
            <h2 className="h4 mb-1">{userData.first_name} {userData.last_name}</h2>
            <p className="text-muted mb-1">{userData.user_email}</p>
          </div>
        </div>

        <div className='d-flex' style={{gap:8}}>
          <Button color="danger" className="mb-4 mt-4" onClick={deleteUser} style={{ width: '6rem' }} disabled={userData?.status == 'Deleted' ? true : false}>
            {userData?.status == 'Deleted' ? 'Deleted' : 'Delete'}
          </Button>
           <Button color="primary" className="mb-4 mt-4" onClick={toggleModal} style={{ width: '6rem' }}>
            Update
          </Button>
        </div>
      </div>

      <Row className="mb-4 p-3">
        <Col md={3}>
          <h6 className="text-muted mb-1">Customer since</h6>
          <p className="font-weight-medium">{userData.created}</p>
        </Col>
        <Col md={3}>
          <h6 className="text-muted mb-1">Last log in</h6>
          <p className="font-weight-medium">{lastLoggedIn}</p>
        </Col>
        <Col md={3}>
          <h6 className="text-muted mb-1">Last Accessed</h6>
          <p className="font-weight-medium">{lastUsed}</p>
        </Col>
      </Row>

      <Row className="mb-4 p-3">
        <Col md={3}>
          <h6 className="text-muted mb-1">Status</h6>
          <p className="font-weight-medium text-capitalize">{overviewUserData.status}</p>
        </Col>
        <Col md={3}>
          <h6 className="text-muted mb-1">Platform</h6>
          <p className="font-weight-medium text-capitalize">{overviewUserData.device_type}</p>
        </Col>
        <Col md={3}>
          <h6 className="text-muted mb-1">User ID</h6>
          <p className="font-weight-medium">{userData.user_id}</p>
        </Col>
        <Col md={3}>
          <h6 className="text-muted mb-1">Analytics</h6>
          <Link to={{
            pathname: groupMemberView ? '/group/member-details/analytics' : '/operations/UserAnalytics',
            state: {
              values: userData,
              userReport: false,
              returnPath: groupMemberView ? '/group/member-details/profile' : undefined
            }
          }}>
            View
          </Link>
        </Col>
      </Row>

      <UserUpdateModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        userData={editProfileUserData}
        onUpdate={handleUpdateSuccess}
      />

      <AddCommentModal
        isOpen={isCommentModalOpen}
        toggle={() => setIsCommentModalOpen(false)}
        onSubmit={handleDeleteCommentSubmit}
        actionType="user-delete"
      />

      <AddCommentModal
        isOpen={isResubscribeCommentModalOpen}
        toggle={() => {
          setIsResubscribeCommentModalOpen(false);
          setPendingResubscribeData(null);
        }}
        onSubmit={handleResubscribeCommentSubmit}
        actionType="resubscribe"
      />

      <PricingPlanModal
        isOpen={showPlanModal}
        toggle={() => {
          if (isResubscribeInProgress) {
            return;
          }
          setShowPlanModal(false);
          setSelectedPlan(null);
          setResubscribeWarning(defaultResubscribeWarning);
        }}
        plans={pricingPlans}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        onConfirm={handleResubscribeConfirm}
        warning={resubscribeWarning}
        isConfirmLoading={isResubscribeInProgress}
      />
    </>
  );
};

export default ProfileEditTab;
